/**
 * Event Bus for decoupled system communication
 * Type-safe event emission and listening
 */

import { GameEvent, GameEventPayload, EventHandler } from '@/types';

class EventBus {
  private listeners: Map<string, Set<EventHandler>> = new Map();

  /**
   * Subscribe to an event
   * @returns Unsubscribe function
   */
  on<E extends GameEvent>(
    event: E,
    handler: EventHandler<GameEventPayload[E]>
  ): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(handler as EventHandler);

    // Return unsubscribe function
    return () => this.off(event, handler);
  }

  /**
   * Unsubscribe from an event
   */
  off<E extends GameEvent>(
    event: E,
    handler: EventHandler<GameEventPayload[E]>
  ): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler as EventHandler);
    }
  }

  /**
   * Emit an event
   */
  emit<E extends GameEvent>(
    event: E,
    payload: GameEventPayload[E]
  ): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(payload));
    }
  }

  /**
   * Subscribe to event once, then auto-unsubscribe
   */
  once<E extends GameEvent>(
    event: E,
    handler: EventHandler<GameEventPayload[E]>
  ): void {
    const wrappedHandler = (payload: GameEventPayload[E]) => {
      handler(payload);
      this.off(event, wrappedHandler);
    };

    this.on(event, wrappedHandler);
  }

  /**
   * Clear all listeners for an event (or all events)
   */
  clear(event?: GameEvent): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Get listener count for debugging
   */
  getListenerCount(event: GameEvent): number {
    return this.listeners.get(event)?.size ?? 0;
  }
}

// Singleton instance
export const eventBus = new EventBus();
